import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Index from "./pages/Index";
import LanguageRedirect from "./pages/LanguageRedirect";
import { ScrollToTop } from "./components/ScrollToTop";
import { CookieBanner } from "./components/CookieBanner";

const OfferPage = lazy(() => import("./pages/OfferPage"));
const WorkPage = lazy(() => import("./pages/WorkPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const WhitepaperPage = lazy(() => import("./pages/WhitepaperPage"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./pages/DatenschutzPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function AppRoutes() {
  return (
    <LanguageProvider>
      <ScrollToTop />
      <Toaster />
      <Sonner />
      <CookieBanner />
      <Suspense fallback={null}>
        <Routes>
          {/* Root redirect based on browser language */}
          <Route path="/" element={<LanguageRedirect />} />
          
          {/* Language-prefixed routes */}
          <Route path="/:lang" element={<Index />} />
          <Route path="/:lang/offer" element={<OfferPage />} />
          <Route path="/:lang/work" element={<WorkPage />} />
          <Route path="/:lang/team" element={<TeamPage />} />
          <Route path="/:lang/faq" element={<FaqPage />} />
          <Route path="/:lang/contact" element={<ContactPage />} />
          <Route path="/:lang/whitepaper" element={<WhitepaperPage />} />
          <Route path="/:lang/blog" element={<BlogPage />} />
          <Route path="/:lang/blog/:slug" element={<BlogPostPage />} />
          <Route path="/:lang/impressum" element={<ImpressumPage />} />
          <Route path="/:lang/datenschutz" element={<DatenschutzPage />} />
          
          {/* Legacy routes without language prefix → redirect to /de */}
          <Route path="/whitepaper" element={<Navigate to="/de/whitepaper" replace />} />
          <Route path="/blog" element={<Navigate to="/de/blog" replace />} />
          <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
          <Route path="/impressum" element={<Navigate to="/de/impressum" replace />} />
          <Route path="/datenschutz" element={<Navigate to="/de/datenschutz" replace />} />
          
          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </LanguageProvider>
  );
}

function LegacyBlogRedirect() {
  const slug = window.location.pathname.split("/blog/")[1];
  return <Navigate to={`/de/blog/${slug}`} replace />;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
